import { useEffect, useMemo, useState } from 'react';
import { getMyAgencyApplications } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Inbox, Search, X } from 'lucide-react';
import {
  EmptyState,
  StatusBadge,
  formatDate,
  fullName,
  safeList,
  statusLabel,
} from './AgencyPortalShared';

const statusFilters = ['all', 'applied', 'under_review', 'offered', 'enrolled', 'waitlisted', 'rejected'];

export default function AgencyReferrals() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await getMyAgencyApplications();
        if (!cancelled) setItems(res.data || []);
      } catch (err) {
        console.error(err);
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    return safeList(items).filter((item) => {
      if (status !== 'all' && item.status !== status) return false;
      if (!q) return true;
      return [
        fullName(item),
        item.email,
        item.phone,
        item.program,
        item.intended_semester,
        item.application_id,
        item.status,
      ].filter(Boolean).join(' ').toLowerCase().includes(q);
    });
  }, [items, query, status]);
  const hasSearch = Boolean(query.trim());
  const hasActiveFilters = hasSearch || status !== 'all';

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h2 className="text-2xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
          Referrals
        </h2>
        <p className="mt-1 text-sm text-[#062B1A]/60">Applications connected to your agency.</p>
      </div>

      <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative w-full lg:max-w-md">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#062B1A]/35" />
              <Input
                type="search"
                aria-label="Search referrals"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search name, email, phone, program, or application..."
                className="h-10 border-[rgba(6,43,26,0.16)] pl-9 pr-9 focus-visible:border-[#D4AF37] focus-visible:ring-[3px] focus-visible:ring-[rgba(212,175,55,0.35)] [&::-webkit-search-cancel-button]:appearance-none"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  aria-label="Clear referral search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-[#062B1A]/40 hover:bg-[#062B1A]/5 hover:text-[#062B1A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]/50"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {statusFilters.map((filter) => (
                <Button
                  key={filter}
                  type="button"
                  size="sm"
                  variant={status === filter ? 'default' : 'outline'}
                  onClick={() => setStatus(filter)}
                  className={status === filter
                    ? 'bg-[#062B1A] text-white hover:bg-[#041B10]'
                    : 'border-[rgba(6,43,26,0.16)] text-[#062B1A] hover:bg-[#F7F8FA]'}
                >
                  {filter === 'all' ? 'All' : statusLabel(filter)}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
            Referral List
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((item) => (
                <div key={item} className="h-16 animate-pulse rounded-lg bg-[#F7F8FA]" />
              ))}
            </div>
          ) : filteredItems.length === 0 ? (
            <EmptyState icon={Inbox} title={hasActiveFilters ? 'No referrals match the current filters' : 'No referrals found'}>
              {hasSearch
                ? `No referrals match “${query.trim()}”. Clear the search or choose another status.`
                : status !== 'all' ? `There are no ${statusLabel(status).toLowerCase()} referrals.` : undefined}
            </EmptyState>
          ) : (
            <div className="overflow-hidden rounded-lg border border-[rgba(6,43,26,0.08)]">
              <div className="hidden grid-cols-[1.2fr_1.4fr_0.9fr_0.8fr_0.8fr] gap-4 bg-[#F7F8FA] px-4 py-3 text-xs font-semibold uppercase tracking-[0.08em] text-[#062B1A]/45 lg:grid">
                <span>Applicant</span>
                <span>Program</span>
                <span>Intake</span>
                <span>Status</span>
                <span>Submitted</span>
              </div>
              <div className="divide-y divide-[rgba(6,43,26,0.06)]">
                {filteredItems.map((item) => (
                  <div key={item.id} className="grid gap-3 px-4 py-4 lg:grid-cols-[1.2fr_1.4fr_0.9fr_0.8fr_0.8fr] lg:items-center lg:gap-4">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-[#062B1A]">{fullName(item)}</p>
                      <p className="truncate text-xs text-[#062B1A]/50">{item.email || 'No email'}</p>
                    </div>
                    <p className="text-sm text-[#062B1A]">{item.program || 'Program not set'}</p>
                    <p className="text-sm text-[#062B1A]/65">{item.intended_semester || 'Not set'}</p>
                    <div>
                      <StatusBadge status={item.status} />
                    </div>
                    <p className="text-sm text-[#062B1A]/65">{formatDate(item.submitted_at)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
